import { useEffect, useState } from "react";

import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { reloadPage } from "utils/reload-page.util";

// Long enough not to fire on a slow-but-working chunk fetch, short enough that
// nobody sits behind a dead spinner wondering whether the dialog is broken.
const SLOW_LOAD_MS = 10000;

// Suspense fallback for a lazily-loaded dialog (EmailForm / FaxForm /
// AppointmentForm). Rendered BEFORE the real dialog's own MUI <Dialog> has
// mounted, so this owns its own minimal visual rather than assuming any
// dialog chrome exists yet.
//
// Escalates after SLOW_LOAD_MS instead of spinning forever: `lazyWithRetry`'s
// auto-reload can be vetoed by a `beforeunload` guard (Ambient Scribe
// recording, campaign wizard, leads workflow), and a chunk request a proxy
// holds open never rejects at all — neither case is visible to
// DialogLoadErrorBoundary, since nothing has thrown. This is the one place
// that can offer a way out.
export default function DialogLoadingFallback() {
  const [isSlow, setIsSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Backdrop
      open
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
      aria-hidden={false}
    >
      {isSlow ? (
        <Alert severity="warning" sx={{ maxWidth: 480 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is taking longer than expected. Try reloading the page.
          </Typography>
          <Button variant="contained" size="small" onClick={reloadPage}>
            Reload page
          </Button>
        </Alert>
      ) : (
        <CircularProgress color="inherit" aria-hidden={false} />
      )}
    </Backdrop>
  );
}
